import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Switch } from "@/race-tracker/components/ui/switch";
import { Label } from "@/race-tracker/components/ui/label";
import { Input } from "@/race-tracker/components/ui/input";
import { Search, X } from "lucide-react";
import { Button } from "@/race-tracker/components/ui/button";

const SWEEP_STALENESS_OPTIONS = [
  { value: "warning", label: "Dig. Sweep >7 days" },
  { value: "danger",  label: "Dig. Sweep >14 days" },
  { value: "critical",label: "Dig. Sweep >30 days" },
];

const ANALYSTS = ["Dan", "Evan", "Ian", "James"];
const TEAM_MEMBERS = ["Lauren", "Paul", "Kyle", "Rachel", "Kiera", "Casey", "Rande", "Max"];
const AGENCIES = ["SMG", "DCM", "DRM", "SMG/DCM", "No Client"];
const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];
const CHAMBERS = ["U.S. House", "U.S. Senate", "State Senate", "State House", "Governor", "Other"];
const ELECTION_TYPES = ["Primary", "General", "Runoff", "Special"];

export default function FilterBar({ filters, onChange }) {
  const update = (key, value) => onChange({ ...filters, [key]: value });

  const hasActiveFilters = filters.search || filters.da !== "all" || filters.ae !== "all" ||
    filters.am !== "all" || filters.state !== "all" || filters.chamber !== "all" ||
    filters.election_type !== "all" || filters.status !== "Active" || filters.smg_plus ||
    filters.agency !== "all" || filters.sweep_staleness !== "all";

  const clearAll = () => onChange({
    search: "", da: "all", ae: "all", am: "all", state: "all",
    chamber: "all", election_type: "all", status: "Active", smg_plus: false, agency: "all",
    sweep_staleness: "all",
  });

  return (
    <div className="space-y-3">
      {/* Search Row */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search advertisers, races..."
            value={filters.search || ""}
            onChange={(e) => update("search", e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs gap-1 text-muted-foreground">
            <X className="w-3 h-3" /> Clear filters
          </Button>
        )}
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="Status" value={filters.status} onValueChange={(v) => update("status", v)} options={["Active", "Inactive", "Done"]} allLabel="All" />
        <FilterSelect label="Agency" value={filters.agency} onValueChange={(v) => update("agency", v)} options={AGENCIES} />
        <FilterSelect label="Analyst" value={filters.da} onValueChange={(v) => update("da", v)} options={ANALYSTS} />
        <FilterSelect label="AE" value={filters.ae} onValueChange={(v) => update("ae", v)} options={TEAM_MEMBERS} />
        <FilterSelect label="AM" value={filters.am} onValueChange={(v) => update("am", v)} options={TEAM_MEMBERS} />
        <FilterSelect label="State" value={filters.state} onValueChange={(v) => update("state", v)} options={STATES} />
        <FilterSelect label="Chamber" value={filters.chamber} onValueChange={(v) => update("chamber", v)} options={CHAMBERS} />
        <FilterSelect label="Election" value={filters.election_type} onValueChange={(v) => update("election_type", v)} options={ELECTION_TYPES} />
        <Select value={filters.sweep_staleness || "all"} onValueChange={(v) => update("sweep_staleness", v)}>
          <SelectTrigger className="h-8 text-xs w-auto min-w-[140px] gap-1">
            <SelectValue placeholder="Dig. Sweep" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dig. Sweep</SelectItem>
            {SWEEP_STALENESS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1.5 ml-2">
          <Switch
            id="smg-plus"
            checked={filters.smg_plus || false}
            onCheckedChange={(v) => update("smg_plus", v)}
            className="scale-75"
          />
          <Label htmlFor="smg-plus" className="text-xs font-medium text-muted-foreground cursor-pointer">
            SMG+ only
          </Label>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onValueChange, options, allLabel = "All" }) {
  return (
    <Select value={value || "all"} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 text-xs w-auto min-w-[100px] gap-1">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel} {label}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}